const IMAGE_PRELOAD_RETRIES = 3;

/**
 * Downloads a file 
 * @param {String} fileUrl
 * @param {String} fileName
 */
export const download = async (fileUrl, fileName) => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(url);
  } catch (e) {
    throw new Error(e);
  }
};

/**
 * Preload an imageurl or data url
 * @param {string} image        url or dataurl
 * @param {boolean} crossOrigin should this add the cross origin flag
 *                              (only do this for non dataurls)
 * @param {number} [retries=IMAGE_PRELOAD_RETRIES] retries the fetch
 * @return {Promise}
 */
export function preloadImage(image, crossOrigin, retries) {
  const preloader = new Image();
  if (crossOrigin) {
    preloader.crossOrigin = 'anonymous';
  }
  let currentRetries = retries !== undefined ? retries : IMAGE_PRELOAD_RETRIES;
  const promise = new Promise((resolve, reject) => {
    preloader.addEventListener('load', () => resolve(preloader));
    // retry on error for retry count;
    preloader.addEventListener('error', () =>
      // eslint-disable-next-line no-plusplus
      currentRetries-- > 0
        ? preloadImage(image, crossOrigin, currentRetries)
        : reject(new Error(`preloading failed for ${image}`)),
    );
    preloader.src = image;
  });
  return promise;
}

/**
 * Caches already created canvases in memory for performance.
 * @returns {function}
 */
export const getOrCreateCanvas = () => {
  const canvases = {};
  return (id, options = {}) => {
    const { width = 512, height = 512, createContext = true } = options;
    if (!canvases[id]) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      let context;
      if (createContext) {
        context = canvas.getContext('2d');
        canvases[id] = { canvas, context };
      } else {
        canvases[id] = { canvas };
      }
    }

    // Clear canvas before using again
    if (createContext && canvases[id].context) {
      canvases[id].context.clearRect(0, 0, width, height);
    }
    return canvases[id];
  };
};

/**
 * Produces an image or a canvas element that masks the artwork image 
 * with provided material
 * @param {ImageData|HTMLCanvasElement|HTMLImageElement} artworkImages
 * @param {ImageData|HTMLCanvasElement|HTMLImageElement} materialImage
 * @param {Function} getCanvas cache function
 * @param {Object} options
 * @param {string} options.outputFormat
 * @param {boolean} options.invert
 * @param {number} options.shadowDepth
 * @returns {string|HTMLCanvasElement}
 */
export const renderDesignView = (artworkImage, materialImage, getCanvas, options = {}) => {
  const { outputFormat, invert = false, shadowDepth } = options;
  const { canvas: artworkLayer, context: artworkCtx } = getCanvas('artworkLayer');

  artworkCtx.drawImage(artworkImage, 0, 0, artworkLayer.width, artworkLayer.height);

  // Create second canvas and draw material image
  const {
    canvas: materialBackgroundLayer,
    context: materialCtx
  } = getCanvas('materialBackgroundLayer');

  materialCtx.drawImage(
    materialImage,
    0,
    0,
    materialBackgroundLayer.width,
    materialBackgroundLayer.height,
  );

  // Set composite operation to 'destination-in' and draw first 
  // canvas onto second canvas 'destination-out' for inverse effect
  materialCtx.globalCompositeOperation = invert ? 'destination-out' : 'destination-in';
  materialCtx.drawImage(artworkLayer, 0, 0);

  // Reset composite operation
  materialCtx.globalCompositeOperation = 'source-over';

  // Combine shadow and material-artwork composite
  const { canvas: viewLayer, context: viewCtx } = getCanvas('viewLayer');

  // Set shadow filter
  // eslint-disable-next-line max-len
  viewCtx.filter = `drop-shadow(0px ${shadowDepth}px 0px rgba(30, 20, 0, 1)) drop-shadow(1px 3px 3px rgba(0, 0, 50, 0.8))`;
  viewCtx.drawImage(materialBackgroundLayer, 0, 0);

  if (outputFormat === 'canvas') return viewLayer;

  return viewLayer.toDataURL('image/png');
};

